import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { LogOut, Package, ShoppingCart, History, Plus, Minus, Loader2, Calendar, Edit2, X, Printer, KeyRound, Info, CheckCircle2, Settings as SettingsIcon, CreditCard } from "lucide-react";
import { MyLinenSection } from "@/components/MyLinenSection";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import InvoicePrint from "@/components/InvoicePrint";
import { generateInvoicePdf } from "@/utils/invoicePdf";
import { useCompanyInfo } from "@/hooks/useCompanyInfo";
import SetupCardDialog from "@/pages/admin/billing/SetupCardDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const statusConfig = {
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  approved: { label: "Approved", color: "bg-blue-100 text-blue-800 border-blue-200" },
  dispatched: { label: "Dispatched", color: "bg-green-100 text-green-800 border-green-200" },
  invoiced: { label: "Invoicing", color: "bg-purple-100 text-purple-800 border-purple-200" },
  canceled: { label: "Canceled", color: "bg-red-100 text-red-800 border-red-200" },
  // Legacy statuses
  packed: { label: "Packed", color: "bg-blue-100 text-blue-800 border-blue-200" },
  confirmed: { label: "Confirmed", color: "bg-blue-100 text-blue-800 border-blue-200" },
  shipped: { label: "Shipped", color: "bg-green-100 text-green-800 border-green-200" },
};

export default function CustomerDashboard({ user, onLogout }) {
  const { companyInfo } = useCompanyInfo();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [cart, setCart] = useState({});
  const [notes, setNotes] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [availableDates, setAvailableDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("new-order");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [editingOrder, setEditingOrder] = useState(null);
  const [editCart, setEditCart] = useState({});
  const [editNotes, setEditNotes] = useState("");
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [cancellingOrder, setCancellingOrder] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [viewingOrder, setViewingOrder] = useState(null);
  const [showOrderDetailsDialog, setShowOrderDetailsDialog] = useState(false);
  const [printingOrder, setPrintingOrder] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  // Per-order flag so clicking Download Invoice on one card doesn't
  // grey-out (or accidentally re-trigger) the buttons on the other cards.
  const [downloadingOrderId, setDownloadingOrderId] = useState(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  // Multi-hotel: list of hotels this user owns + currently active selection.
  // null means "All hotels" — orders from every hotel are visible.
  const [myHotels, setMyHotels] = useState([]);
  const [selectedHotelId, setSelectedHotelId] = useState(null);
  // Settings tab — hotel currently being managed in the card dialog.
  const [cardHotel, setCardHotel] = useState(null);
  // Overdue-invoice popup (7+ days, across ALL hotels on this account).
  const [overdueInvoices, setOverdueInvoices] = useState([]);
  const [overdueOpen, setOverdueOpen] = useState(false);
  const [payingOrderId, setPayingOrderId] = useState(null);

  useEffect(() => {
    fetchUserProducts();
    fetchOrders();
    fetchDeliveryDates();
    fetchMyHotels();
    fetchOverdue();
  }, []);

  const fetchOverdue = async () => {
    try {
      const res = await axios.get(`${API}/billing/my/overdue`, { headers: { ...getAuthHeader(), "x-no-cache": "1" } });
      const inv = res.data?.invoices || [];
      setOverdueInvoices(inv);
      if (inv.length > 0) setOverdueOpen(true);
    } catch { /* non-blocking */ }
  };

  const handlePayNow = async (inv) => {
    setPayingOrderId(inv.order_id);
    try {
      const res = await axios.post(`${API}/billing/my/orders/${inv.order_id}/pay`, {}, { headers: getAuthHeader() });
      if (res.data?.ok) {
        toast.success(`Invoice ${inv.order_number} paid — thank you!`);
        const rest = overdueInvoices.filter((x) => x.order_id !== inv.order_id);
        setOverdueInvoices(rest);
        if (rest.length === 0) setOverdueOpen(false);
        fetchOrders();
      } else {
        toast.error(res.data?.error || "Payment failed — please check your card");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment failed");
    } finally {
      setPayingOrderId(null);
    }
  };

  const handleViewInvoice = (inv) => {
    setOverdueOpen(false);
    if (inv.hotel_id) setSelectedHotelId(inv.hotel_id);
    setStatusFilter("invoiced");
    setActiveTab("order-history");
  };

  // Re-fetch products when the customer switches between their allocated hotels
  // so prices/catalogue can differ between, say, a city site and a remote one.
  // The cart MUST be cleared too: the new hotel may not stock some selected
  // products and unit prices can differ per hotel.
  useEffect(() => {
    if (selectedHotelId) {
      fetchUserProducts();
      if (Object.keys(cart).length > 0) {
        setCart({});
        toast.info("Cart cleared — products and prices are set per hotel");
      }
    }
    // eslint-disable-next-line
  }, [selectedHotelId]);

  const fetchMyHotels = async () => {
    try {
      const res = await axios.get(`${API}/users/${user.id}/hotels`, { headers: getAuthHeader() });
      const list = res.data || [];
      setMyHotels(list);
      // Default to the primary hotel (or first one) on initial load.
      const primary = list.find((h) => h.is_primary) || list[0];
      if (primary && selectedHotelId === null) setSelectedHotelId(primary.id);
    } catch (e) {
      console.warn("Failed to load hotels", e);
    }
  };

  const fetchUserProducts = async () => {
    try {
      // Scope the product fetch to the currently-selected hotel so customers
      // with multiple hotels see the right catalogue/pricing per hotel.
      const params = selectedHotelId ? `?hotel_id=${encodeURIComponent(selectedHotelId)}` : "";
      const response = await axios.get(`${API}/users/${user.id}/products${params}`, {
        headers: getAuthHeader(),
      });
      
      if (response.data.products && response.data.products.length > 0) {
        const allProductsRes = await axios.get(`${API}/products`, {
          headers: getAuthHeader(),
        });
        
        // Create maps for price and sort_order
        const userProductsMap = {};
        const sortOrderMap = {};
        response.data.products.forEach(p => {
          userProductsMap[p.product_id] = p.price;
          sortOrderMap[p.product_id] = p.sort_order !== undefined ? p.sort_order : 999;
        });
        
        const userProducts = allProductsRes.data
          .filter(p => userProductsMap[p.id] !== undefined)
          .map(p => ({
            ...p,
            price_per_unit: userProductsMap[p.id],
            sort_order: sortOrderMap[p.id]
          }))
          // Sort alphabetically (case-insensitive) by product name
          .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
        
        setProducts(userProducts);
      }
    } catch (error) {
      console.error("Failed to load products:", error);
      toast.error("Failed to load products");
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: getAuthHeader(),
      });
      let ordersList = [];
      if (response.data.orders) {
        ordersList = response.data.orders;
      } else if (response.data.groups) {
        ordersList = Object.values(response.data.groups).flat();
      }
      
      // Sort by delivery date DESCENDING — nearest / latest delivery date on
      // top, matching the admin OrdersTab convention. Hotels open the app
      // and see their upcoming (or most recent) deliveries first without
      // scrolling past historical orders.
      ordersList.sort((a, b) => {
        const dateA = new Date(a.delivery_date);
        const dateB = new Date(b.delivery_date);
        return dateB - dateA;
      });
      
      setOrders(ordersList);
    } catch (error) {
      toast.error("Failed to load orders");
    }
  };

  const fetchDeliveryDates = async () => {
    try {
      const response = await axios.get(`${API}/delivery-dates/${user.id}`, {
        headers: getAuthHeader(),
      });
      
      // Filter dates based on deadline: must be before 11:00 AM on day before delivery
      const now = new Date();
      const validDates = (response.data.dates || []).filter(dateStr => {
        const deliveryDate = new Date(dateStr);
        const deadline = new Date(deliveryDate);
        deadline.setDate(deadline.getDate() - 1);
        deadline.setHours(11, 0, 0, 0);
        return now < deadline;
      });
      
      setAvailableDates(validDates);
      if (validDates.length > 0) {
        setDeliveryDate(validDates[0]);
      }
    } catch (error) {
      console.error("Failed to load delivery dates:", error);
    }
  };

  const handleDownloadInvoice = async (order) => {
    // Guard against a rapid double-click: run per-order (not a shared flag),
    // so clicking on one card can't accidentally spin up others.
    if (downloadingOrderId) return;
    setDownloadingOrderId(order.id);
    const dismissId = toast.loading("Fetching invoice…");
    try {
      // Prefer the Xero-rendered PDF when the invoice has been synced —
      // matches the customer-facing document they see in their inbox and
      // avoids drift from our client-side generator.
      if (order.xero_invoice_id) {
        const res = await axios.get(
          `${API}/billing/orders/${order.id}/invoice-pdf-customer`,
          { responseType: "blob", headers: getAuthHeader() },
        );
        if (!(res.data instanceof Blob) || res.data.size === 0) {
          throw new Error("Empty invoice PDF received");
        }
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${order.order_number || "invoice"}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success("Invoice downloaded", { id: dismissId });
      } else {
        // Fallback for orders that haven't been synced to Xero yet (e.g.
        // still in dispatched, or Xero was down when we tried).
        await generateInvoicePdf(order);
        toast.success("PDF downloaded", { id: dismissId });
      }
    } catch (error) {
      console.error("PDF download failed:", error);
      // Surface backend detail when available (e.g. "Invoice not synced yet").
      const detail = error?.response?.data?.detail
        || (error?.response?.status === 403 ? "Not authorized to fetch this invoice" : null)
        || error?.message
        || "Failed to fetch invoice";
      toast.error(detail, { id: dismissId });
    } finally {
      setDownloadingOrderId(null);
    }
  };

  const updateCart = (product, delta) => {
    const currentQty = cart[product.id]?.quantity || 0;
    const newQty = Math.max(0, currentQty + delta);

    if (newQty === 0) {
      const newCart = { ...cart };
      delete newCart[product.id];
      setCart(newCart);
    } else if (newQty >= product.minimum_quantity || newQty === 0) {
      setCart({
        ...cart,
        [product.id]: {
          product_id: product.id,
          product_name: product.name,
          quantity: newQty,
          price_per_unit: product.price_per_unit,
        },
      });
    } else {
      toast.warning(`Minimum order quantity for ${product.name} is ${product.minimum_quantity}`);
    }
  };

  const handlePlaceOrder = async () => {
    const items = Object.values(cart);
    if (items.length === 0) {
      toast.error("Please add items to cart");
      return;
    }

    if (!deliveryDate) {
      toast.error("Please select a delivery date");
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/orders`,
        { items, delivery_date: deliveryDate, hotel_id: selectedHotelId || null },
        { headers: getAuthHeader() }
      );
      toast.success("Order placed successfully!");
      setCart({});
      setNotes("");
      fetchOrders();
      setActiveTab("order-history");
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' 
        ? errorDetail 
        : errorDetail?.msg || "Failed to place order";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const isOrderEditable = (deliveryDate) => {
    const delivery = new Date(deliveryDate);
    const now = new Date();
    const deadline = new Date(delivery);
    deadline.setDate(deadline.getDate() - 1);
    deadline.setHours(11, 0, 0, 0); // 11:00 AM on day before delivery
    
    return now < deadline;
  };

  const openEditDialog = (order) => {
    if (!isOrderEditable(order.delivery_date)) {
      toast.error("Order deadline has passed. Orders can only be edited before 11:00 AM on the day before delivery.");
      return;
    }
    
    setEditingOrder(order);
    const newEditCart = {};
    order.items.forEach(item => {
      newEditCart[item.product_id] = {
        ...item,
        quantity: item.quantity,
        price_per_unit: item.price_per_unit
      };
    });
    setEditCart(newEditCart);
    setEditNotes(order.notes || "");
    setShowEditDialog(true);
  };

  const updateEditCart = (product, delta) => {
    const currentQty = editCart[product.id]?.quantity || 0;
    const newQty = Math.max(0, currentQty + delta);

    if (newQty === 0) {
      const newCart = { ...editCart };
      delete newCart[product.id];
      setEditCart(newCart);
    } else {
      setEditCart({
        ...editCart,
        [product.id]: {
          product_id: product.id,
          product_name: product.name,
          quantity: newQty,
          price_per_unit: product.price_per_unit
        }
      });
    }
  };

  const handleSaveEdit = async () => {
    const items = Object.values(editCart);
    
    if (items.length === 0) {
      toast.error("Please add at least one item");
      return;
    }

    setLoading(true);
    try {
      await axios.put(
        `${API}/orders/${editingOrder.id}/customer-edit`,
        { 
          items, 
          notes: editNotes, 
          delivery_date: editingOrder.delivery_date,
        },
        { headers: getAuthHeader() }
      );
      toast.success("Order updated successfully!");
      setShowEditDialog(false);
      setEditingOrder(null);
      setEditCart({});
      setEditNotes("");
      fetchOrders();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' 
        ? errorDetail 
        : errorDetail?.msg || "Failed to update order";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const cartTotal = Object.values(cart).reduce(
    (sum, item) => sum + item.quantity * item.price_per_unit,
    0
  );

  const editCartTotal = Object.values(editCart).reduce(
    (sum, item) => sum + item.quantity * item.price_per_unit,
    0
  );

  const handleCancelOrder = async () => {
    if (!cancellingOrder) return;
    
    setLoading(true);
    try {
      await axios.delete(
        `${API}/orders/${cancellingOrder.id}/customer-cancel`,
        { headers: getAuthHeader() }
      );
      toast.success("Order cancelled successfully!");
      setShowCancelDialog(false);
      setCancellingOrder(null);
      fetchOrders();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' 
        ? errorDetail 
        : errorDetail?.msg || "Failed to cancel order";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <div className="bg-brand-navy p-2 rounded-sm shrink-0">
                <Package className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-base sm:text-2xl font-bold text-gray-900 truncate">{companyInfo.system_name}</h1>
                <p className="text-xs sm:text-sm text-gray-600 truncate">
                  {selectedHotelId && myHotels.find((h) => h.id === selectedHotelId)?.hotel_name
                    || user.hotel_name}
                </p>
              </div>
              {myHotels.length > 1 && (
                <div className="ml-1 sm:ml-4 flex items-center gap-2 shrink-0">
                  <select
                    data-testid="customer-hotel-switcher"
                    value={selectedHotelId || ""}
                    onChange={(e) => setSelectedHotelId(e.target.value)}
                    className="text-xs sm:text-sm border-2 border-brand-navy/30 rounded-md px-1.5 sm:px-2 py-1 bg-white font-semibold text-brand-navy focus:border-brand-navy outline-none max-w-[120px] sm:max-w-none"
                  >
                    {myHotels.map((h) => (
                      <option key={h.id} value={h.id}>
                        {h.hotel_name}{h.is_primary ? "  ★" : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="flex items-center gap-1 sm:gap-2 shrink-0">
              <Button
                onClick={() => setChangePasswordOpen(true)}
                data-testid="change-password-button"
                variant="outline"
                size="sm"
                className="flex items-center gap-2 px-2 sm:px-3"
              >
                <KeyRound className="w-4 h-4" />
                <span className="hidden sm:inline">Password</span>
              </Button>
              <Button
                onClick={onLogout}
                data-testid="logout-button"
                variant="outline"
                size="sm"
                className="flex items-center gap-2 px-2 sm:px-3"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Announcement Banner */}
        <div className="mb-6">
          <AnnouncementBanner />
        </div>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full max-w-3xl mx-auto grid-cols-2 sm:grid-cols-4 h-auto sm:h-12 gap-1">
            <TabsTrigger value="new-order" data-testid="new-order-tab" className="text-sm sm:text-base py-2">
              <ShoppingCart className="w-4 h-4 mr-1.5 sm:mr-2" />
              New Order
            </TabsTrigger>
            <TabsTrigger value="order-history" data-testid="order-history-tab" className="text-sm sm:text-base py-2">
              <History className="w-4 h-4 mr-1.5 sm:mr-2" />
              Order History
            </TabsTrigger>
            <TabsTrigger value="my-linen" data-testid="my-linen-tab" className="text-sm sm:text-base py-2">
              <Package className="w-4 h-4 mr-1.5 sm:mr-2" />
              My Linen
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="settings-tab" className="text-sm sm:text-base py-2">
              <SettingsIcon className="w-4 h-4 mr-1.5 sm:mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="my-linen" className="fade-in">
            {activeTab === "my-linen" && <MyLinenSection hotelId={selectedHotelId} />}
          </TabsContent>

          <TabsContent value="new-order" className="space-y-6 fade-in">
            {products.length === 0 ? (
              <Card className="shadow-lg border-0">
                <CardContent className="p-12 text-center">
                  <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500 text-lg">No products assigned yet</p>
                  <p className="text-gray-400 text-sm mt-2">Please contact admin to assign products to your account</p>
                </CardContent>
              </Card>
            ) : (
              <>
                <Card className="shadow-lg border-0">
                  <CardHeader>
                    <CardTitle className="text-2xl">Available Products</CardTitle>
                    <CardDescription>Select items and quantities for your order</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {products.map((product) => (
                        <Card key={product.id} data-testid={`product-${product.id}`} className="card-hover border-2">
                          <CardContent className="p-6">
                            <h3 className="font-semibold text-lg mb-4">{product.name}</h3>
                            <p className="text-sm text-gray-600 mb-4">
                              Min. order: {product.minimum_quantity} units
                            </p>
                            <div className="flex items-center space-x-2">
                              <Button
                                onClick={() => updateCart(product, -product.minimum_quantity)}
                                data-testid={`decrease-${product.id}`}
                                variant="outline"
                                size="icon"
                                className="h-10 w-10 rounded-full"
                              >
                                <Minus className="w-4 h-4" />
                              </Button>
                              <Input
                                type="number"
                                value={cart[product.id]?.quantity || 0}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value) || 0;
                                  if (val === 0) {
                                    const newCart = { ...cart };
                                    delete newCart[product.id];
                                    setCart(newCart);
                                  } else {
                                    setCart({
                                      ...cart,
                                      [product.id]: {
                                        product_id: product.id,
                                        product_name: product.name,
                                        quantity: val,
                                        price_per_unit: product.price_per_unit
                                      }
                                    });
                                  }
                                }}
                                onBlur={(e) => {
                                  const val = parseInt(e.target.value) || 0;
                                  const minQty = product.minimum_quantity;
                                  if (val > 0 && val < minQty) {
                                    toast.warning(`${product.name} requires minimum ${minQty} units`);
                                  } else if (val > 0 && val % minQty !== 0) {
                                    toast.warning(`${product.name} quantity must be a multiple of ${minQty} (e.g., ${minQty}, ${minQty*2}, ${minQty*3}...)`);
                                  }
                                }}
                                min="0"
                                step={product.minimum_quantity}
                                className="w-20 text-center font-semibold text-lg"
                                data-testid={`quantity-${product.id}`}
                              />
                              <Button
                                onClick={() => updateCart(product, product.minimum_quantity)}
                                data-testid={`increase-${product.id}`}
                                size="icon"
                                className="h-10 w-10 rounded-none bg-brand-navy hover:bg-brand-navy-hover"
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {Object.keys(cart).length > 0 && (
                  <Card className="shadow-lg border-0 fade-in">
                    <CardHeader>
                      <CardTitle className="text-2xl">Order Summary</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        {Object.values(cart).map((item) => (
                          <div key={item.product_id} data-testid={`cart-item-${item.product_id}`} className="flex justify-between items-center py-2 border-b">
                            <div>
                              <p className="font-medium">{item.product_name}</p>
                              <p className="text-sm text-gray-600">
                                Quantity: {item.quantity}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="pt-4 border-t-2">
                        <div className="space-y-4 mb-4">
                          <div>
                            <Label htmlFor="delivery-date" className="flex items-center mb-2">
                              <Calendar className="w-4 h-4 mr-2" />
                              Delivery Date
                            </Label>
                            {availableDates.length > 0 ? (
                              <>
                                <Select value={deliveryDate} onValueChange={setDeliveryDate}>
                                  <SelectTrigger data-testid="delivery-date-select">
                                    <SelectValue placeholder="Select delivery date" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {availableDates.map((date) => (
                                      <SelectItem key={date} value={date}>
                                        {new Date(date).toLocaleDateString("en-US", {
                                          weekday: "long",
                                          year: "numeric",
                                          month: "long",
                                          day: "numeric",
                                        })}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {deliveryDate && (
                                  <p className="text-xs text-orange-600 mt-1 bg-orange-50 p-2 rounded border border-orange-200">
                                    ⏰ Order deadline: {new Date(new Date(deliveryDate).getTime() - 86400000).toLocaleDateString("en-US", {
                                      month: "short",
                                      day: "numeric"
                                    })} at 11:00 AM
                                  </p>
                                )}
                              </>
                            ) : (
                              <p className="text-sm text-red-600">No available delivery dates. Please contact admin.</p>
                            )}
                          </div>
                        </div>

                        <Button
                          onClick={handlePlaceOrder}
                          data-testid="place-order-button"
                          disabled={loading || availableDates.length === 0}
                          className="w-full h-12 text-base font-medium bg-brand-navy hover:bg-brand-navy-hover rounded-sm"
                        >
                          {loading ? (
                            <>
                              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                              Placing Order...
                            </>
                          ) : (
                            "Place Order"
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="order-history" className="fade-in">
            <Card className="shadow-lg border-0">
              <CardHeader>
                <CardTitle className="text-2xl">Your Orders</CardTitle>
                <CardDescription>View all your previous orders</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Status Filter Buttons */}
                <div className="flex gap-2 flex-wrap mb-6">
                  {(() => {
                    // Hotel filter applied first; status filter operates on the result.
                    const hotelFiltered = selectedHotelId
                      ? orders.filter((o) => o.hotel_id === selectedHotelId)
                      : orders;
                    return (
                      <>
                        <Button
                          onClick={() => setStatusFilter("pending")}
                          variant={statusFilter === "pending" ? "default" : "outline"}
                          className={statusFilter === "pending" ? "bg-yellow-600" : ""}
                          size="sm"
                        >
                          Pending ({hotelFiltered.filter(o => o.status === "pending").length})
                        </Button>
                        <Button
                          onClick={() => setStatusFilter("approved")}
                          variant={statusFilter === "approved" ? "default" : "outline"}
                          className={statusFilter === "approved" ? "bg-blue-600" : ""}
                          size="sm"
                        >
                          Approved ({hotelFiltered.filter(o => o.status === "approved").length})
                        </Button>
                        <Button
                          onClick={() => setStatusFilter("dispatched")}
                          variant={statusFilter === "dispatched" ? "default" : "outline"}
                          className={statusFilter === "dispatched" ? "bg-green-600" : ""}
                          size="sm"
                        >
                          Dispatched ({hotelFiltered.filter(o => o.status === "dispatched").length})
                        </Button>
                        <Button
                          onClick={() => setStatusFilter("invoiced")}
                          variant={statusFilter === "invoiced" ? "default" : "outline"}
                          className={statusFilter === "invoiced" ? "bg-purple-600" : ""}
                          size="sm"
                        >
                          Invoiced ({hotelFiltered.filter(o => o.status === "invoiced").length})
                        </Button>
                        <Button
                          onClick={() => setStatusFilter("canceled")}
                          variant={statusFilter === "canceled" ? "default" : "outline"}
                          className={statusFilter === "canceled" ? "bg-red-600" : ""}
                          size="sm"
                        >
                          Canceled ({hotelFiltered.filter(o => o.status === "canceled").length})
                        </Button>
                        <Button
                          onClick={() => setStatusFilter("all")}
                          variant={statusFilter === "all" ? "default" : "outline"}
                          size="sm"
                        >
                          All ({hotelFiltered.length})
                        </Button>
                      </>
                    );
                  })()}
                </div>

                <div className="space-y-4">
                  {/* Customer-facing notice for the Invoiced tab — explains
                      that this view is a convenience cache only and may
                      auto-purge older invoices over time. */}
                  {statusFilter === "invoiced" && (
                    <div
                      data-testid="invoice-retention-notice"
                      className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs sm:text-sm text-amber-900 flex gap-2 items-start"
                    >
                      <Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-700" />
                      <p className="leading-snug">
                        Invoices shown here are for convenient lookup only and will be
                        automatically removed after a period of time. If you can&apos;t find
                        the invoice you&apos;re looking for, please contact your administrator.
                      </p>
                    </div>
                  )}
                  {(() => {
                    const hotelFiltered = selectedHotelId
                      ? orders.filter((o) => o.hotel_id === selectedHotelId)
                      : orders;
                    const visible = hotelFiltered.filter(o => statusFilter === "all" || o.status === statusFilter);
                    if (visible.length === 0) {
                      return (
                        <div className="text-center py-12">
                          <History className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                          <p className="text-gray-500 text-lg">
                            {statusFilter === "all" ? "No orders yet" : `No ${statusFilter} orders`}
                          </p>
                        </div>
                      );
                    }
                    return visible.map((order) => (
                      <Card key={order.id} data-testid={`order-${order.id}`} className="border-2 card-hover">
                        <CardContent className="p-4 sm:p-6">
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-4 gap-2">
                            <div>
                              {/* Primary heading: DELIVERY date — that's what
                                  hotels actually plan around. Creation time
                                  moved to a small "Ordered" subtext so the
                                  card ordering (by delivery date DESC) is
                                  self-explanatory. */}
                              <p className="text-sm font-semibold text-blue-700 mb-1 flex items-center">
                                <Calendar className="w-4 h-4 mr-1.5" />
                                Delivery: {new Date(order.delivery_date).toLocaleDateString("en-US", {
                                  weekday: "long",
                                  month: "long",
                                  day: "numeric",
                                  year: "numeric",
                                })}
                              </p>
                              <p className="font-semibold text-gray-800">Order #{order.order_number || order.id.slice(0, 8)}</p>
                              <p className="text-xs text-gray-400 mt-1">
                                Ordered {new Date(order.created_at).toLocaleDateString("en-US", {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </p>
                            </div>
                          </div>

                          <div className="space-y-2 mb-4">
                            {order.items.map((item, idx) => (
                              <div key={idx} className="flex justify-between text-sm">
                                <span className="text-gray-700">
                                  {item.product_name} × {item.quantity}
                                </span>
                              </div>
                            ))}
                          </div>

                          {order.status === "invoiced" && (
                            <div className="mb-4 pt-3 border-t flex justify-between items-baseline gap-2">
                              <div>
                                <p className="text-sm font-semibold text-gray-700">Total</p>
                                <p className="text-xs text-gray-500">(includes 10% GST)</p>
                              </div>
                              <span
                                className="text-xl sm:text-2xl font-bold text-brand-navy whitespace-nowrap"
                                data-testid={`order-total-${order.id}`}
                              >
                                ${order.total_price.toFixed(2)}
                              </span>
                            </div>
                          )}

                          {order.notes && (
                            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                              <p className="text-sm text-gray-600">
                                <span className="font-medium">Notes:</span> {order.notes}
                              </p>
                            </div>
                          )}

                          <div className="pt-4 border-t">
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge
                                className={`${statusConfig[order.status]?.color || "bg-gray-100 text-gray-800 border-gray-200"} border status-badge`}
                                data-testid={`order-status-${order.id}`}
                              >
                                {statusConfig[order.status]?.label || order.status}
                              </Badge>
                              {order.status === "pending" && order.created_by && order.created_by !== user.id && (
                                <Badge
                                  variant="outline"
                                  className="text-amber-700 border-amber-300 bg-amber-50"
                                  data-testid={`order-staff-created-badge-${order.id}`}
                                  title="This order was created by staff on your behalf. Contact us for changes."
                                >
                                  <Info className="w-3 h-3 mr-1" />
                                  Created by staff
                                </Badge>
                              )}
                              {/* Payment status badge — surfaces on `invoiced` orders so hotels
                                  never overpay. `billing_status === "collected"` means the
                                  invoice is fully settled; otherwise, if any partial payments
                                  exist, show the running total so the hotel sees how much is
                                  still owed. */}
                              {order.status === "invoiced" && (() => {
                                const total = Number(order.total_price) || 0;
                                const paid = (order.partial_payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
                                const remaining = Math.max(0, total - paid);
                                if (order.billing_status === "collected" || (paid > 0 && remaining <= 0.01)) {
                                  return (
                                    <Badge
                                      className="bg-emerald-600 text-white border-emerald-600"
                                      data-testid={`order-paid-badge-${order.id}`}
                                      title={`Fully paid — $${total.toFixed(2)}`}
                                    >
                                      <CheckCircle2 className="w-3 h-3 mr-1" />
                                      Paid
                                    </Badge>
                                  );
                                }
                                if (paid > 0) {
                                  return (
                                    <Badge
                                      variant="outline"
                                      className="text-amber-800 border-amber-400 bg-amber-50"
                                      data-testid={`order-partial-paid-badge-${order.id}`}
                                      title={`Partially paid — $${paid.toFixed(2)} of $${total.toFixed(2)}. Remaining $${remaining.toFixed(2)}.`}
                                    >
                                      <Info className="w-3 h-3 mr-1" />
                                      Partially paid&nbsp;
                                      <span className="font-semibold">${paid.toFixed(2)}</span>
                                      <span className="opacity-70">&nbsp;/ ${total.toFixed(2)}</span>
                                    </Badge>
                                  );
                                }
                                return null;
                              })()}
                              {order.status === "pending" && isOrderEditable(order.delivery_date) && (!order.created_by || order.created_by === user.id) && (
                                <>
                                  <Button
                                    onClick={() => openEditDialog(order)}
                                    variant="outline"
                                    size="sm"
                                    className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                    data-testid={`order-edit-btn-${order.id}`}
                                  >
                                    <Edit2 className="w-3 h-3 mr-1" />
                                    Edit
                                  </Button>
                                  <Button
                                    onClick={() => {
                                      setCancellingOrder(order);
                                      setShowCancelDialog(true);
                                    }}
                                    variant="outline"
                                    size="sm"
                                    className="text-red-600 border-red-300 hover:bg-red-50"
                                  >
                                    <X className="w-3 h-3 mr-1" />
                                    Cancel
                                  </Button>
                                </>
                              )}
                              {order.status === "invoiced" && (
                                <>
                                  <Button
                                    onClick={() => {
                                      setViewingOrder(order);
                                      setShowOrderDetailsDialog(true);
                                    }}
                                    variant="outline"
                                    size="sm"
                                    className="text-purple-600 border-purple-300 hover:bg-purple-50"
                                  >
                                    <Package className="w-3 h-3 mr-1" />
                                    View Details
                                  </Button>
                                  <Button
                                    onClick={() => handleDownloadInvoice(order)}
                                    variant="outline"
                                    size="sm"
                                    disabled={downloadingOrderId === order.id}
                                    data-testid={`download-invoice-btn-${order.id}`}
                                    className="text-blue-600 border-blue-300 hover:bg-blue-50 disabled:opacity-50"
                                  >
                                    {downloadingOrderId === order.id ? (
                                      <>
                                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                        Downloading…
                                      </>
                                    ) : (
                                      <>
                                        <Printer className="w-3 h-3 mr-1" />
                                        Download Invoice
                                      </>
                                    )}
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ));
                  })()}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings" className="fade-in">
            <Card className="shadow-lg border-0" data-testid="settings-panel">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-brand-navy">
                  <CreditCard className="w-5 h-5" />
                  Payment cards
                </CardTitle>
                <CardDescription>
                  Manage the cards saved for <span className="font-semibold">{myHotels.find((h) => h.id === selectedHotelId)?.hotel_name || "this hotel"}</span>.
                  To change a card, add the new one first, then remove the old one — at least one card must remain on file.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {(() => {
                  const h = myHotels.find((x) => x.id === selectedHotelId);
                  if (!h) {
                    return <p className="text-sm text-neutral-500 py-6 text-center">Select a hotel above to manage its cards.</p>;
                  }
                  return (
                    <div
                      className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-md border p-3 bg-white"
                      data-testid={`settings-hotel-row-${h.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-brand-navy truncate">{h.hotel_name}</span>
                          {h.is_primary && (
                            <span className="text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">Primary</span>
                          )}
                        </div>
                        <div className="text-xs text-neutral-500 truncate">
                          {h.hotel_address || "(no address)"}
                          {h.suburb ? ` · ${h.suburb}` : ""}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 sm:justify-end">
                        <span
                          className={
                            "inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wider border " +
                            (h.has_card
                              ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                              : "bg-neutral-100 text-neutral-500 border-neutral-300")
                          }
                          data-testid={`settings-card-status-${h.id}`}
                        >
                          <CreditCard className="w-3 h-3" />
                          {h.has_card ? "Card on file" : "No card"}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-brand-navy text-brand-navy hover:bg-brand-navy/5"
                          onClick={() => setCardHotel(h)}
                          data-testid={`settings-manage-cards-${h.id}`}
                        >
                          Manage cards
                        </Button>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Customer card management dialog — hotel owners self-serve card add/remove */}
      <SetupCardDialog
        open={!!cardHotel}
        onOpenChange={(v) => {
          if (!v) {
            setCardHotel(null);
            // Refresh hotels so the "Card on file" badge reflects any change.
            fetchMyHotels();
          }
        }}
        hotel={cardHotel}
      />

      {/* Edit Order Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Order #{editingOrder?.order_number}</DialogTitle>
            <DialogDescription>
              Modify your pending order. Deadline: 11:00 AM on the day before delivery.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <p className="text-sm font-medium text-blue-800">
                Delivery: {editingOrder && new Date(editingOrder.delivery_date).toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                  year: "numeric"
                })}
              </p>
            </div>

            <div className="space-y-3">
              <h3 className="font-semibold">Products</h3>
              {products.map((product) => (
                <div key={product.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium">{product.name}</p>
                    <p className="text-xs text-gray-500">Min: {product.minimum_quantity} units</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      onClick={() => updateEditCart(product, -product.minimum_quantity)}
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <Input
                      type="number"
                      value={editCart[product.id]?.quantity || 0}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        if (val === 0) {
                          const newCart = { ...editCart };
                          delete newCart[product.id];
                          setEditCart(newCart);
                        } else {
                          setEditCart({
                            ...editCart,
                            [product.id]: {
                              product_id: product.id,
                              product_name: product.name,
                              quantity: val,
                              price_per_unit: product.price_per_unit
                            }
                          });
                        }
                      }}
                      onBlur={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        const minQty = product.minimum_quantity;
                        if (val > 0 && val < minQty) {
                          toast.warning(`${product.name} requires minimum ${minQty} units`);
                        } else if (val > 0 && val % minQty !== 0) {
                          toast.warning(`${product.name} quantity must be a multiple of ${minQty} (e.g., ${minQty}, ${minQty*2}, ${minQty*3}...)`);
                        }
                      }}
                      min="0"
                      step={product.minimum_quantity}
                      className="w-16 text-center font-medium"
                    />
                    <Button
                      onClick={() => updateEditCart(product, product.minimum_quantity)}
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button
                onClick={() => {
                  setShowEditDialog(false);
                  setEditingOrder(null);
                  setEditCart({});
                }}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEdit}
                disabled={loading || Object.keys(editCart).length === 0}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Order Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel Order</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this order? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {cancellingOrder && (
            <div className="py-4">
              <p className="font-medium mb-2">Order #{cancellingOrder.order_number}</p>
              <p className="text-sm text-gray-600">
                Delivery: {new Date(cancellingOrder.delivery_date).toLocaleDateString("en-US", {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                })}
              </p>
              <div className="mt-3 space-y-1">
                {cancellingOrder.items.map((item, idx) => (
                  <p key={idx} className="text-sm text-gray-700">
                    • {item.product_name} × {item.quantity}
                  </p>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              onClick={() => {
                setShowCancelDialog(false);
                setCancellingOrder(null);
              }}
              variant="outline"
            >
              Keep Order
            </Button>
            <Button
              onClick={handleCancelOrder}
              disabled={loading}
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                <>
                  <X className="w-4 h-4 mr-2" />
                  Cancel Order
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Order Details Dialog (for invoiced orders) */}
      <Dialog open={showOrderDetailsDialog} onOpenChange={setShowOrderDetailsDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Invoice Details</DialogTitle>
            <DialogDescription>
              Complete breakdown of Order #{viewingOrder?.order_number}
            </DialogDescription>
          </DialogHeader>
          
          {viewingOrder && (
            <div className="space-y-4">
              {/* Order Info */}
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-600 font-medium">Order Number</p>
                    <p className="font-semibold text-lg text-purple-700">#{viewingOrder.order_number}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 font-medium">Status</p>
                    <Badge className={`${statusConfig[viewingOrder.status]?.color || "bg-gray-100"} mt-1`}>
                      {statusConfig[viewingOrder.status]?.label || viewingOrder.status}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-gray-600 font-medium">Delivery Date</p>
                    <p className="font-semibold">
                      {new Date(viewingOrder.delivery_date).toLocaleDateString("en-US", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600 font-medium">Order Date</p>
                    <p className="font-semibold">
                      {new Date(viewingOrder.created_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Items */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Items</h3>
                <div className="space-y-2">
                  {viewingOrder.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border">
                      <div className="flex-1">
                        <p className="font-medium text-gray-800">{item.product_name}</p>
                        <p className="text-sm text-gray-600">
                          ${item.price_per_unit.toFixed(2)} × {item.quantity}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-lg">
                          ${(item.quantity * item.price_per_unit).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Adjustments (if any) */}
              {viewingOrder.adjustments && viewingOrder.adjustments.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg mb-3">Adjustments</h3>
                  <div className="space-y-2">
                    {viewingOrder.adjustments.map((adj, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg border border-yellow-200" data-testid={`customer-adjustment-${idx}`}>
                        <div className="flex-1">
                          <p className="font-medium text-gray-800">{adj.description}</p>
                          <p className="text-xs text-gray-500">
                            {(adj.quantity || 1) > 1 || adj.unit_amount != null
                              ? `${adj.quantity || 1} × $${(adj.unit_amount != null ? adj.unit_amount : Math.abs(adj.amount) / (adj.quantity || 1)).toFixed(2)}`
                              : "Price adjustment"}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`font-semibold text-lg ${adj.amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {adj.amount >= 0 ? '+' : ''}${adj.amount.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes (if any) */}
              {viewingOrder.notes && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-medium text-gray-700 mb-1">Notes:</p>
                  <p className="text-sm text-gray-600">{viewingOrder.notes}</p>
                </div>
              )}

              {/* Price Breakdown */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-lg mb-3">Price Breakdown</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Items Subtotal:</span>
                    <span className="font-semibold">
                      ${viewingOrder.items.reduce((sum, item) => sum + item.quantity * item.price_per_unit, 0).toFixed(2)}
                    </span>
                  </div>
                  
                  {viewingOrder.adjustments && viewingOrder.adjustments.length > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Adjustments:</span>
                      <span className={`font-semibold ${viewingOrder.adjustments.reduce((sum, adj) => sum + adj.amount, 0) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {viewingOrder.adjustments.reduce((sum, adj) => sum + adj.amount, 0) >= 0 ? '+' : ''}
                        ${viewingOrder.adjustments.reduce((sum, adj) => sum + adj.amount, 0).toFixed(2)}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal (before GST):</span>
                    <span className="font-semibold">
                      ${(viewingOrder.items.reduce((sum, item) => sum + item.quantity * item.price_per_unit, 0) + 
                        (viewingOrder.adjustments ? viewingOrder.adjustments.reduce((sum, adj) => sum + adj.amount, 0) : 0)).toFixed(2)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">GST (10%):</span>
                    <span className="font-semibold">
                      ${((viewingOrder.items.reduce((sum, item) => sum + item.quantity * item.price_per_unit, 0) + 
                        (viewingOrder.adjustments ? viewingOrder.adjustments.reduce((sum, adj) => sum + adj.amount, 0) : 0)) * 0.10).toFixed(2)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center pt-3 border-t">
                    <span className="font-semibold text-lg">Total Amount:</span>
                    <span className="text-3xl font-bold text-purple-600">
                      ${viewingOrder.total_price.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 text-right">(includes 10% GST)</p>
                </div>
              </div>

              {/* Close Button */}
              <div className="pt-4 border-t">
                <Button
                  onClick={() => {
                    setShowOrderDetailsDialog(false);
                    setViewingOrder(null);
                  }}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />

      {/* Overdue invoices popup — shown on login when any invoice across this
          account is 7+ days overdue. Pay now always charges the card saved on
          THAT invoice's own hotel. */}
      <Dialog open={overdueOpen} onOpenChange={setOverdueOpen}>
        <DialogContent className="max-w-md" data-testid="overdue-popup">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <Info className="w-5 h-5" /> Overdue invoices
            </DialogTitle>
            <DialogDescription>
              The following invoice{overdueInvoices.length > 1 ? "s are" : " is"} more than 7 days overdue.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[46vh] overflow-y-auto">
            {overdueInvoices.map((inv) => (
              <div
                key={inv.order_id}
                className="border border-red-200 bg-red-50/60 rounded-lg p-3 flex items-center justify-between gap-3"
                data-testid={`overdue-item-${inv.order_id}`}
              >
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate">{inv.hotel_name}</p>
                  <p className="text-xs text-neutral-600 font-mono">{inv.order_number}</p>
                  <p className="text-xs text-red-700 font-semibold">
                    ${inv.outstanding.toFixed(2)} · {inv.days_overdue} days overdue
                  </p>
                </div>
                {inv.payment_mode === "direct_debit" && inv.has_card ? (
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                    disabled={payingOrderId === inv.order_id}
                    onClick={() => handlePayNow(inv)}
                    data-testid={`overdue-pay-now-${inv.order_id}`}
                  >
                    {payingOrderId === inv.order_id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Pay now"}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="shrink-0 border-red-300 text-red-700 hover:bg-red-50"
                    onClick={() => handleViewInvoice(inv)}
                    data-testid={`overdue-view-${inv.order_id}`}
                  >
                    Pay now
                  </Button>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-end pt-1">
            <Button
              variant="outline"
              onClick={() => setOverdueOpen(false)}
              data-testid="overdue-close-btn"
            >
              {overdueInvoices.some((i) => i.payment_mode === "direct_debit" && i.has_card) ? "I'll pay later" : "Close"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
